import { ReactElement, useEffect, useMemo } from 'react';
import { useLocation, useParams, Navigate } from 'react-router-dom';
import { BreadcrumbHeading, Spinner, Tab, TabContent, TabList, TabsProvider } from '@weareopenr/component-library';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { useItem } from '../../hooks/useItem/useItem';
import { ItemType } from 'src/constants';
import useBreadcrumbs from 'src/hooks/useBreadcrumbs/useBreadcrumbs';
import ProductDetailsTab from 'src/components/ProductDetailsTab';
import styles from './ProductDisplay.module.scss';
import ProductRecipeTab from 'src/components/ProductRecipeTab';
import PricingTab from 'src/components/PricingTab';
import OptionGroupTab from 'src/components/OptionGroupTab';
import RulesPanelTab from 'src/components/RulesPanel';
import { Routes } from 'src/routes';

export interface IParams {
  itemId: string;
}

const ProductDisplay = (): ReactElement => {
  const { itemId } = useParams<{ itemId?: string }>();

  const { data: item, isLoading } = useItem(itemId);
  const showRecipeTab = item?.type !== ItemType.BUNDLE && !!item?.recipe;
  const isBundle = item?.type === ItemType.BUNDLE;

  const itemName = item?.name;

  const generatedBreadcrumbs = useBreadcrumbs();

  const { pathname } = useLocation();

  const breadcrumbs = useMemo(
    () =>
      itemName ? [...generatedBreadcrumbs.slice(0, -1), { path: pathname, label: itemName }] : generatedBreadcrumbs,
    [pathname, itemName],
  );

  useEffect(() => {
    document.title = item?.name || 'Openr';
  }, [item]);

  return isLoading ? (
    <SpinnerContainer data-testid="loadingIcon">
      <Spinner size="xlarge" colour="rainbow" />
    </SpinnerContainer>
  ) : !itemId || !item ? (
    <Navigate to={Routes.PRODUCT_LIBRARY} replace />
  ) : (
    <TabsProvider defaultTab="product-details">
      <div className={styles['page-container']}>
        <BreadcrumbHeading breadcrumbs={breadcrumbs} />
        <TabList className={styles['tab-list']}>
          <Tab id="product-details" tabText="Product Details" />
          <Tab id="pricing" tabText="Pricing" />
          {item?.type !== ItemType.MODIFIER && (
            <Tab id="modifiers" tabText={`${isBundle ? 'Bundle categories' : 'Modifiers'}`} />
          )}
          {showRecipeTab && <Tab id="recipe" tabText="Recipe" />}
          <Tab id="rules" tabText="Rules" />
        </TabList>
        <TabContent id="product-details">
          <ProductDetailsTab itemId={itemId} />
        </TabContent>
        <TabContent id="modifiers">
          <OptionGroupTab itemId={itemId} />
        </TabContent>
        <TabContent id="recipe">
          <ProductRecipeTab itemId={itemId} />
        </TabContent>
        <TabContent id="pricing">
          <PricingTab itemId={itemId} />
        </TabContent>
        <TabContent id="rules">
          <RulesPanelTab itemId={itemId} />
        </TabContent>
      </div>
    </TabsProvider>
  );
};

export default ProductDisplay;
